<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap ma-0 mb-2 pa-0>
      <v-flex pt-0>
        <v-layout>
          <template>
            <h2><span class="grey--text">{{ paymentType === 'ach' ? 'ACH' : 'Card' }}</span> Installment Transactions</h2>
            <Lightbulb medium>
              <template v-slot:message>
                <span>This screen will allow you to search for installment transactions using the custom search button. The Option button next to any transaction allows you to Refund, Reprint/Re-Email a receipt, recharge a customer and transactions details.</span>
              </template>
            </Lightbulb>
          </template>
        </v-layout>
      </v-flex>
    </v-layout>

    <div>
      <v-data-table
        :headers="columnsDisplayed"
        :items="rows"
        :pagination.sync="pagination"
        :loading="loading"
        :total-items="totalRows"
        :rows-per-page-items="pageCountOptions"
        item-key="id"
        class="elevation-1"
        must-sort
        no-data-text="No data found"
        no-results-text="No results found"
        @update:pagination="search"
      >
        <template slot="headers" slot-scope="props">
          <tr>
            <th
              v-for="(header) in props.headers"
              :key="header.value"
              v-show="header.visible"
              :class="['font-weight-bold text-xs-left column sortable pa-1 ma-0', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
              @click="changeSort(header.value)"
            >
              {{ header.text }}
              <v-icon small>arrow_upward</v-icon>
            </th>
            <th class="ma-0 pa-0 text-xs-right">
              <v-btn icon flat color="primary" @click="reset">
                <v-icon>fa-refresh</v-icon>
              </v-btn>
            </th>
          </tr>
        </template>

        <template slot="items" slot-scope="props">
          <tr>
            <td
              v-for="(header) in columnsDisplayed"
              :key="header.value"
              v-show="header.visible"
              class="pa-1 ma-0"
            >
              <template v-if="header.value === 'total_amount'">
                <span v-if="props.item.modify_total_amount">{{ moneyNoSign(props.item.modify_total_amount / 100) }}</span>
                <span v-else>{{ moneyNoSign(props.item.total_amount / 100) }}</span>
              </template>

              <template v-else-if="header.value === 'net_amount' || header.value === 'less_fee_amount'">
                <span v-if="props.item.request_type==='credit' || props.item.request_type==='achcredit' || props.item.request_type==='void' || props.item.request_type==='achvoid'">({{ moneyNoSign((props.item.total_amount - props.item.fee_amount) / 100) }})</span>
                <span v-else>{{ moneyNoSign((props.item.total_amount - props.item.fee_amount) / 100) }}</span>
              </template>

              <template v-else-if="header.value === 'card_expiration_yymm'">
                {{ expiration(props.item.card_expiration_yymm) }}
              </template>

              <template v-else-if="header.value === 'ach_is_authorized'">
                <span class="font-weight-bold red--text" v-if="!props.item.ach_is_authorized">No</span>
                <span v-else>Yes</span>
              </template>

              <template v-else-if="header.type === 'boolean'">
                <span v-if="props.item[header.value]">Yes</span>
                <span v-else>No</span>
              </template>

              <template v-else-if="header.type === 'money'">
                {{ moneyNoSign(props.item[header.value] / 100) }}
              </template>

              <template v-else-if="header.type === 'date'">
                {{ dateUtc(props.item[header.value]) }}
              </template>

              <template v-else class="text-capitalize">
                {{ props.item[header.value] }}
              </template>
            </td>
            <td class="ma-0 pa-0 text-xs-right">
              <v-btn small v-if="props.item.transaction_group_custom_tracking_id" :to="{ name: 'PlanManage', query: { id: props.item.transaction_group_custom_tracking_id }}">Options</v-btn>
            </td>
          </tr>
        </template>

        <template slot="no-data">
          No results found
        </template>
      </v-data-table>
    </div>

    <v-layout row wrap justify-end pr-1>
      <v-btn color="primary" class="mb-2" @click="searchModal = true">
        Search
        <v-divider vertical class="ml-2 mr-1" v-if="hasPermission('has_inline_help')"></v-divider>
        <Lightbulb color="primary--text">
          <template v-slot:message>
            <span>Use the fields or buttons below to search specific transactions or run reports</span>
          </template>
        </Lightbulb>
      </v-btn>
      <!-- <v-btn class="mb-2" @click="exportFull">Export</v-btn> -->
    </v-layout>

    <v-dialog scrollable v-model="searchModal" @submit.prevent @keydown.esc.prevent="searchModal=false">
      <v-card>
        <v-toolbar dense flat>
          <v-toolbar-title>
            Search {{ paymentType === 'ach' ? 'ACH' : 'Card' }} Installments
            <Lightbulb medium>
              <template v-slot:message>
                <span>This screen will allow you to search for transactions using the custom search button. The Option button next to any transaction allows you to Refund, Reprint/Re-Email a receipt, recharge a customer and transactions details.</span>
              </template>
            </Lightbulb>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon flat color="primary" @click.native="searchModal = false">
            <v-icon>fa-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider class="primary"></v-divider>

        <v-card-text>
          <v-form @keyup.native.enter="searchNew">
            <v-container fluid pa-0>
              <v-layout row wrap>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Customer Name"
                    v-model="filter.customer_name"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Last Four"
                    v-model="filter.last_four"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Amount"
                    v-model="filter.amount"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-menu
                    ref="startDateModal"
                    :close-on-content-click="false"
                    v-model="startDateModal"
                    :nudge-right="40"
                    :return-value.sync="filter.start_date"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <v-text-field
                      slot="activator"
                      v-model="formattedStartDate"
                      label="Created Start"
                      append-icon="event"
                      box
                      hide-details
                    />
                    <v-date-picker
                      v-model="filter.start_date"
                      no-title
                      @input="$refs.startDateModal.save(filter.start_date)"
                    />
                  </v-menu>
                  <v-menu
                    ref="endDateModal"
                    :close-on-content-click="false"
                    v-model="endDateModal"
                    :nudge-right="40"
                    :return-value.sync="filter.end_date"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <v-text-field
                      slot="activator"
                      v-model="formattedEndDate"
                      label="Created End"
                      append-icon="event"
                      box
                      hide-details
                    />
                    <v-date-picker
                      v-model="filter.end_date"
                      no-title
                      @input="$refs.endDateModal.save(filter.end_date)"
                    />
                  </v-menu>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Customer PO"
                    v-model="filter.customer_po"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Custom 1"
                    v-model="filter.custom_field_1"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Custom 2"
                    v-model="filter.custom_field_2"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Custom 3"
                    v-model="filter.custom_field_3"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Notes"
                    v-model="filter.notes"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3 v-if="paymentType === 'ach' && terminalsACH.length > 1">
                  <v-autocomplete
                    name="terminal"
                    label="Terminal"
                    v-model="filter.terminal_id"
                    :items="terminals"
                    item-text="name"
                    item-value="terminal_id"
                    box
                    hide-details
                  ></v-autocomplete>
                </v-flex>
                <v-flex xs6 sm3 v-if="paymentType !== 'ach' && terminalsCard.length > 1">
                  <v-autocomplete
                    name="terminal"
                    label="Terminal"
                    v-model="filter.terminal_id"
                    :items="terminals"
                    item-text="name"
                    item-value="terminal_id"
                    box
                    hide-details
                  ></v-autocomplete>
                </v-flex>
                <v-flex xs6 sm3>
                  <AccountSelect
                    v-model="filter.account_id"
                    multiple
                    label="Account"
                    box
                    hide-details
                    v-if="hasPermission('superuser') || myProfile.accounts.length > 1"
                  />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="MID"
                    v-model="filter.mid"
                    box
                    hide-details
                  />
                </v-flex>
              </v-layout>

              <v-divider class="primary" />

              <v-layout row wrap justify-end>
                <v-btn color="primary" @click="searchNew">Search</v-btn>
                <v-btn @click="reset">Clear</v-btn>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Moment from 'moment'

import Bus from '@/bus'

import Lightbulb from '@/components/help/Lightbulb'
import AccountSelect from '@/components/common/AccountSelect.vue'
import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import DisplayMixin from '@/mixins/display'
import ExportMixin from '@/mixins/export'
import StoreMixin from '@/store/mixin'

export default {
  components: {Lightbulb, AccountSelect},

  mixins: [ApiMixin, AuthMixin, DisplayMixin, ExportMixin, StoreMixin],

  data: function () {
    return {
      booleanSelect: [
        {value: '*', text: 'All'},
        {value: '0', text: 'No'},
        {value: '1', text: 'Yes'}
      ],

      // requestTypes: ['All', 'Sale', 'Void', 'Credit'],
      requestTypes: [
        {value: 'Sale', text: 'Sales'},
        {value: 'Void', text: 'Voids'},
        {value: 'Credit', text: 'Refunds'}
      ],

      columns: this.defaultColumns(),
      pagination: this.defaultPagination(),
      rows: [],
      totalRows: 0,
      pageCountOptions: [5, 10, 25],
      loading: true,

      searchModal: false,
      startDateModal: false,
      endDateModal: false,

      filter: this.defaultFilter()
    }
  },

  props: {
    paymentType: String
  },

  // created () {
  //   Bus.$on('chargeSuccessful', this.search)
  //   Bus.$on('optionsClose', this.search)
  // },

  beforeDestroy () {
    Bus.$off('chargeSuccessful')
    Bus.$off('optionsClose')
  },

  mounted () {
    Bus.$on('chargeSuccessful', this.search)
    Bus.$on('optionsClose', this.search)
  },

  destroyed () {
    this.$store.commit('clearErrors')
    this.$store.commit('clearInfo')
  },

  computed: {
    environment () {
      return process.env.NODE_ENV
    },

    terminals: function () {
      let terminals = [
        {terminal_id: '*', name: 'All'}
      ]
      if (this.paymentType === 'ach') {
        return terminals.concat(this.terminalsACH)
      } else {
        return terminals.concat(this.terminalsCard)
      }
    },

    columnsDisplayed: function () {
      return this.columns.filter(column => {
        if (column.value === 'ach_is_authorized' && this.paymentType === 'ach') {
          column.visible = true
        }

        if (column.value === 'card_expiration_yymm' && this.paymentType === 'ach') {
          column.visible = false
        }

        if (column.value === 'terminal_code' && this.paymentType === 'ach' && this.terminalsACH.length < 2) {
          column.visible = false
        }
        if (column.value === 'terminal_code' && this.paymentType !== 'ach' && this.terminalsCard.length < 2) {
          column.visible = false
        }

        if (column.value === 'account_name' && this.myProfile.accounts.length < 2) {
          column.visible = false
        }

        if (this.myProfile.custom && this.myProfile.custom.hasOwnProperty(this.paymentType === 'ach' ? 'plan_ach_columns' : 'plan_columns')) {
          if (this.myProfile.custom[this.paymentType === 'ach' ? 'plan_ach_columns' : 'plan_columns'].includes(column.value)) {
            column.visible = true
          } else {
            column.visible = false
          }
        }

        if (column.visible) {
          return true
        }

        return false
      }, this)
    },

    columnsExported: function () {
      return this.columns.filter(column => {
        if (column.value === 'ach_is_authorized' && this.paymentType === 'ach') {
          column.export = true
        }

        if (column.value === 'card_expiration_yymm' && this.paymentType === 'ach') {
          column.export = false
        }

        if (column.value === 'terminal_code' && this.paymentType === 'ach' && this.terminalsACH.length < 2) {
          column.export = false
        }
        if (column.value === 'terminal_code' && this.paymentType !== 'ach' && this.terminalsCard.length < 2) {
          column.export = false
        }

        if (column.value === 'account_name' && this.myProfile.accounts.length < 2) {
          column.export = false
        }

        if (this.myProfile.custom && this.myProfile.custom.hasOwnProperty(this.paymentType === 'ach' ? 'plan_ach_export_columns' : 'plan_export_columns')) {
          if (this.myProfile.custom[this.paymentType === 'ach' ? 'plan_ach_export_columns' : 'plan_export_columns'].includes(column.value)) {
            column.export = true
          } else {
            column.export = false
          }
        }

        if (column.export) {
          return true
        }

        return false
      }, this)
    },

    formattedStartDate () {
      return this.filter.start_date ? Moment(this.filter.start_date).format('MMM-DD-YYYY') : ''
    },
    formattedEndDate () {
      return this.filter.end_date ? Moment(this.filter.end_date).format('MMM-DD-YYYY') : ''
    },
    formattedSettledStartDate () {
      return this.filter.settled_start_date ? Moment(this.filter.settled_start_date).format('MMM-DD-YYYY') : ''
    },
    formattedSettledEndDate () {
      return this.filter.settled_end_date ? Moment(this.filter.settled_end_date).format('MMM-DD-YYYY') : ''
    }
  },

  watch: {
    $route (to, from) {
      this.reset()
    }
  },

  methods: {
    defaultColumns () {
      return [
        {
          value: 'transaction_group_id',
          text: 'Group ID',
          sortable: true,
          visible: false,
          export: false
        },
        {
          value: 'customer_name',
          text: 'Cust. Name',
          sortable: true,
          visible: true,
          export: true
        },
        // {
        //   value: 'transaction_status',
        //   text: 'Status',
        //   sortable: true,
        //   visible: true,
        //   export: true
        // },
        {
          value: 'last_four',
          text: 'Last Four',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'card_expiration_yymm',
          text: 'Expiration',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'ach_is_authorized',
          text: 'Authorized',
          sortable: true,
          visible: false,
          export: false,
          type: 'boolean'
        },
        {
          value: 'total_amount',
          text: 'Amount ($)',
          sortable: true,
          visible: true,
          export: true,
          type: 'money'
        },
        {
          value: 'net_amount',
          text: 'Net ($)',
          sortable: true,
          visible: false,
          export: false,
          type: 'money'
        },
        {
          value: 'less_fee_amount',
          text: 'Less Adj ($)',
          sortable: true,
          visible: false,
          export: false,
          type: 'money'
        },
        {
          value: 'fee_amount',
          text: 'Fee ($)',
          sortable: true,
          visible: false,
          export: false,
          type: 'money'
        },
        {
          value: 'type',
          text: 'Frequency',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'start_date',
          text: 'Start',
          sortable: true,
          visible: true,
          export: true,
          type: 'date'
        },
        {
          value: 'end_date',
          text: 'End',
          sortable: true,
          visible: true,
          export: true,
          type: 'date'
        },
        {
          value: 'next_date',
          text: 'Next',
          sortable: true,
          visible: true,
          export: true,
          type: 'date'
        },
        {
          value: 'order_number',
          text: 'Order Number',
          sortable: false,
          visible: false,
          export: false
        },
        {
          value: 'customer_po',
          text: 'Customer PO',
          sortable: false,
          visible: false,
          export: true
        },
        {
          value: 'custom_field_1',
          text: 'Custom 1',
          sortable: false,
          visible: false,
          export: true
        },
        {
          value: 'custom_field_2',
          text: 'Custom 2',
          sortable: false,
          visible: false,
          export: true
        },
        {
          value: 'custom_field_3',
          text: 'Custom 3',
          sortable: false,
          visible: false,
          export: true
        },
        {
          value: 'account_name',
          text: 'Account',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'terminal_name',
          text: 'Terminal',
          sortable: true,
          visible: false,
          export: true
        },
        {
          value: 'terminal_code',
          text: 'Terminal',
          sortable: true,
          visible: true,
          export: true
        }
      ]
    },

    defaultFilter () {
      return {
        account_id: [],
        mid: '',
        amount: '',
        customer_name: '',
        start_date: '',
        end_date: '',
        settled_start_date: '',
        settled_end_date: '',
        is_recurring: '*',
        is_fee_waived: '*',
        last_four: '',
        terminal_id: '*',
        transaction_id: '',
        tracking_id: '',
        customer_po: '',
        custom_field_1: '',
        custom_field_2: '',
        custom_field_3: '',
        notes: ''
      }
    },

    defaultPagination () {
      return {
        sortBy: this.$store.state.paginationDefaults.sortBy,
        descending: this.$store.state.paginationDefaults.descending,
        rowsPerPage: this.$store.state.paginationDefaults.rowsPerPage,
        page: 1,
        totalItems: 0
      }
    },

    async search () {
      this.loading = true

      try {
        let filter = { ...this.filter }

        if (filter.start_date) {
          filter.start_date = Moment(this.filter.start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.end_date) {
          filter.end_date = Moment(Moment(this.filter.end_date).format('YYYY-MM-DD') + ' 23:59:59').utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.settled_start_date) {
          filter.settled_start_date = Moment(this.filter.settled_start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.settled_end_date) {
          filter.settled_end_date = Moment(Moment(this.filter.settled_end_date).format('YYYY-MM-DD') + ' 23:59:59').utc().format('YYYY-MM-DD HH:mm:ss')
        }

        if (this.paymentType === 'ach') {
          filter.request_type = 'ach'
        }

        let result = await this.api({
          method: 'get',
          url: '/plans',
          params: {
            filter: filter,
            page: this.pagination.page - 1,
            perPage: this.pagination.rowsPerPage,
            orderBy: this.pagination.sortBy,
            orderDirection: this.pagination.descending ? 'desc' : 'asc'
          }
        })
        this.rows = result.data.rows
        this.totalRows = result.data.count
        this.pagination.totalItems = result.data.count

        this.searchModal = false
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.loading = false
      }
    },

    async exportFull () {
      try {
        this.loading = true
        let result = await this.api({
          method: 'get',
          url: '/transactions',
          params: {
            filter: this.filter,
            perPage: '*',
            orderBy: this.pagination.sortBy,
            orderDirection: this.pagination.descending ? 'desc' : 'asc'
          }
        })

        let csvContent = 'data:text/csv;charset=utf-8,'
        let headers = [ ...this.headers ]
        headers.push({
          value: 'customer_po',
          text: 'Customer PO'
        })
        headers.push({
          value: 'custom_field_1',
          text: 'Custom 1'
        })
        headers.push({
          value: 'custom_field_2',
          text: 'Custom 2'
        })
        headers.push({
          value: 'custom_field_3',
          text: 'Custom 3'
        })

        csvContent += [
          headers.map(header => '"' + header.text + '"').join(','),
          ...result.data.rows.map(row => {
            return headers.map(header => {
              let value = row[header.value]
              if (header.value === 'is_recurring') {
                value = (value === 0) ? 'No' : 'Yes'
              }
              if (header.value === 'is_fee_waived') {
                value = (value === 0) ? 'No' : 'Yes'
              }
              if (header.value === 'total_amount') {
                value = '$' + (value / 100).toFixed(2)
              }
              if (value === null) {
                value = ''
              }

              return '"' + ('' + value).replace(/"/, '""') + '"'
            })
              .join(',')
          })

        ]
          .join('\n')

        await this.downloadCSVData(csvContent)

        this.loading = false
      } catch (err) {
        // this.$store.commit('error', err)
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      }
    },

    searchNew () {
      this.pagination.page = 1
      this.search()
    },

    reset () {
      this.columns = this.defaultColumns()
      this.filter = this.defaultFilter()
      this.pagination = this.defaultPagination()

      this.search()
    },

    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
      this.search()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Plans.vue